import productionApi from '../../api/restaurant/restaurantProductionInventoryApi';
import movementApi from '../../api/restaurant/restaurantInventoryMovementApi';
import {
  DecimalValue,
  RestaurantInventoryMovementFilters,
  RestaurantInventoryMovementPage,
  RestaurantProductionResponse,
  RestaurantRecipeAvailability,
  RestaurantRecipeConsumption,
  RestaurantRecipeConsumptionRequest,
  RestaurantRecipeCost,
} from '../../types/restaurant/RestaurantProductionInventoryTypes';
import { appendRestaurantQuery } from './restaurantPageQuery';

const recipePath = (branchId: number, recipeId: number) =>
  `/branches/${branchId}/recipes/${recipeId}`;

const RestaurantProductionInventoryService = {
  calculateCost: async (
    branchId: number,
    recipeId: number,
    quantity: DecimalValue
  ) =>
    (
      await productionApi.fetchCustom(
        `${recipePath(branchId, recipeId)}/cost?${new URLSearchParams({
          quantity: String(quantity),
        })}`
      )
    ).data as RestaurantProductionResponse<RestaurantRecipeCost>,

  checkAvailability: async (
    branchId: number,
    recipeId: number,
    quantity: DecimalValue
  ) =>
    (
      await productionApi.addCustom(
        `${recipePath(branchId, recipeId)}/availability`,
        { quantity: String(quantity) }
      )
    ).data as RestaurantProductionResponse<RestaurantRecipeAvailability>,

  /**
   * Disponibilidad de la receta del producto (para avisar en el POS antes de
   * vender). Si el producto no tiene receta devuelve `hasRecipe: false`.
   */
  checkAvailabilityByProduct: async (
    branchId: number,
    productId: number,
    quantity: DecimalValue
  ) =>
    (
      await productionApi.addCustom(
        `/branches/${branchId}/products/${productId}/recipe-availability`,
        { quantity: String(quantity) }
      )
    ).data as RestaurantProductionResponse<RestaurantRecipeAvailability>,

  consume: async (
    branchId: number,
    recipeId: number,
    request: RestaurantRecipeConsumptionRequest
  ) =>
    (
      await productionApi.addCustom(
        `${recipePath(branchId, recipeId)}/consume`,
        { ...request, quantity: String(request.quantity) }
      )
    ).data as RestaurantProductionResponse<RestaurantRecipeConsumption>,

  listMovements: async (
    branchId: number,
    filters: RestaurantInventoryMovementFilters = {}
  ) => {
    const query = appendRestaurantQuery(
      new URLSearchParams({
        page: String(filters.page ?? 0),
        size: String(filters.size ?? 20),
      }),
      {
        search: filters.search?.trim(),
        inventoryId: filters.inventoryId,
        rawMaterialId: filters.rawMaterialId,
        categoryId: filters.categoryId,
        movementType: filters.movementType,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
      }
    );

    return (await movementApi.fetchCustom(`/branches/${branchId}?${query}`))
      .data as RestaurantInventoryMovementPage;
  },
};

export default RestaurantProductionInventoryService;

// Adaptador preparado para restaurant_orders en Sprint 4.
export const consumeRestaurantOrderRecipe = (
  branchId: number,
  recipeId: number,
  orderId: number,
  quantity: DecimalValue,
  event: RestaurantRecipeConsumptionRequest['event']
) =>
  RestaurantProductionInventoryService.consume(branchId, recipeId, {
    quantity,
    event,
    referenceType: 'RESTAURANT_ORDER',
    referenceId: orderId,
  });
